import type { ReactNode } from "react"
import { cn } from "@/lib/utils"

export type UsageCardTone = "primary" | "warning"

export interface UsageCardProps {
  title: ReactNode
  caption?: ReactNode
  percentUsed: number
  used: number
  total: number
  remaining: number
  usedLabel?: string
  remainingLabel?: string
  tone?: UsageCardTone
  className?: string
}

export function UsageCard({
  title,
  caption,
  percentUsed,
  used,
  total,
  remaining,
  usedLabel = "used",
  remainingLabel = "remaining",
  tone = "primary",
  className,
}: UsageCardProps) {
  const pct = Math.max(0, Math.min(100, percentUsed))
  const tint = tone === "warning" ? "bg-warning" : "bg-primary"

  return (
    <div
      className={cn(
        "rounded-md border border-border bg-card p-5 flex flex-col gap-3",
        className,
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-0.5">
          <span className="text-section text-foreground">{title}</span>
          {caption && <span className="text-caption text-muted-foreground">{caption}</span>}
        </div>
        <span className="text-section text-foreground tabular-nums">{pct}%</span>
      </div>

      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={cn(
            "h-full rounded-full motion-safe:transition-[width] motion-safe:duration-500",
            tint,
          )}
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="flex items-center justify-between text-caption text-muted-foreground">
        <span>
          <span className="text-foreground font-medium tabular-nums">
            {used.toLocaleString()}
          </span>
          {" / "}
          <span className="tabular-nums">{total.toLocaleString()}</span>
          {" "}
          {usedLabel}
        </span>
        <span>
          <span className="text-foreground font-medium tabular-nums">
            {remaining.toLocaleString()}
          </span>
          {" "}
          {remainingLabel}
        </span>
      </div>
    </div>
  )
}
